<script>
import Button from "@/components/atoms/button";
import FormRadio from "@/components/molecules/form-radio";
import Badge from "@/components/atoms/badge";
import Card from "@/components/molecules/card";
import StateOverlay from "@/components/molecules/state-overlay";
import PaymentPromoCode from "@/containers/payment-method-promo-code";
import PaymentUseEuroCora from "@/components/organisms/payment-method/payment-method-carte-malin/eurocora/default";
import PaymentCarteMalin from "@/containers/payment-method-no-carte-malin";
import PaymentCarteCarrefour from "@/components/organisms/payment-method/payment-method-carte-pass";
import PaymentCardsRegistered from "@/components/organisms/payment-method/payment-method-cards/registered";
import PaymentMethodRegisterCardCora from "@/containers/payment-method-register-card-cora";
import PaymentMethodCards from "@/components/organisms/payment-method/payment-method-cards";
import PaymentMethodProduitsIncompatibles from "@/components/organisms/payment-method/payment-method-produits-incompatibles/default";
import FormCheckbox from "@/components/molecules/form-checkbox";
import Alert from "@/components/molecules/alert";
import PaymentMethodMultiPay from "@/containers/payment-method-multi-pay";
import Form from "@/components/molecules/form";
import FormGroup from "@/components/molecules/form-group";
import Popin from "@/components/molecules/popin";
import {
  paymentMethodProps,
  usePaymentMethod,
  paymentMethodEmits
} from "./mixin";

export default {
  name: "PaymentMethod",
  emits: [...paymentMethodEmits(), "displayCgv"],
  components: {
    Card,
    FormRadio,
    Button,
    Badge,
    PaymentPromoCode,
    PaymentCarteMalin,
    PaymentCardsRegistered,
    PaymentMethodRegisterCardCora,
    PaymentCarteCarrefour,
    StateOverlay,
    PaymentMethodCards,
    PaymentUseEuroCora,
    PaymentMethodProduitsIncompatibles,
    FormCheckbox,
    Alert,
    PaymentMethodMultiPay,
    Form,
    FormGroup,
    Popin
  },
  props: {
    ...paymentMethodProps()
  },
  setup(props, context) {
    const popinUseEuroCora = ref(null);

    const {
      isInputedEuroCora,
      setInputStatus,
      isMajeur,
      validerDisabled,
      useEuroCora,
      cancelUseEuroCora,
      updateAliasPaiement,
      processPaiement,
      select,
      openPopinMultiPay,
      paymentMethodId,
      paymentMethodIsCarteCora,
      typeCarteEuroCora,
      montantEuroCora,
      showEcheancier,
      nouveau,
      disabledComputed,
      aliasNotSelected
    } = usePaymentMethod(props, context, popinUseEuroCora);

    return {
      isInputedEuroCora,
      setInputStatus,
      popinUseEuroCora,
      isMajeur,
      validerDisabled,
      useEuroCora,
      cancelUseEuroCora,
      updateAliasPaiement,
      processPaiement,
      select,
      openPopinMultiPay,
      paymentMethodId,
      paymentMethodIsCarteCora,
      typeCarteEuroCora,
      montantEuroCora,
      showEcheancier,
      nouveau,
      disabledComputed,
      aliasNotSelected
    };
  }
};
</script>

<template>
  <Card
    :elevation="disabledComputed ? '' : 'md'"
    :disabled="disabledComputed"
    class="c-payment-method"
    :class="disabledComputed && 'c-payment-method--disabled'"
    radius="md"
  >
    <template #header>
      <Button
        :disabled="disabledComputed"
        class="c-payment-method__selector"
        @click="select()"
      >
        <Form>
          <FormGroup>
            <FormRadio
              :disabled="disabledComputed"
              :checked="selected"
              tabindex="-1"
              aria-hidden="true"
              class="c-form-radio--no-label c-payment-method__radio"
            />
          </FormGroup>
        </Form>
        <p class="c-payment-method__label">
          {{ paymentMethod.libelle_court }}
        </p>
        <Badge
          v-if="nouveau && !disabledComputed"
          variant="new"
          radius="md"
          class="u-color-white u-px-xs u-py-xxs u-font-11 u-font-700 u-ml-xs"
        >
          NOUVEAU
        </Badge>
        <!-- <div class="c-payment-method__cards">
        <LazyImage
          :src="paymentMethod.picto"
          :alt="paymentMethod.libelle_court"
        />
      </div> -->
        <PaymentMethodCards
          :is-fid-carrefour="isFidCarrefour"
          :payment-method="paymentMethodId"
          :class="disabledComputed && 'c-payment-method__cards--disabled'"
          width="47"
          height="30"
          class="c-payment-method__cards"
        />
        <p
          v-if="disabledComputed && paymentMethodInfos.raison"
          class="c-payment-method__info"
        >
          {{ paymentMethodInfos.raison }}
        </p>
        <p
          v-else-if="
            paymentMethodIsCarteCora &&
              isPorteurCarteCora &&
              !carteCoraIsMalinPlus &&
              !showEcheancier
          "
          class="c-payment-method__advantage"
        >
          Bénéficiez de tous vos avantages en payant avec votre carte de paiement cora Cpay Mastercard
        </p>
        <div
          v-if="disabledComputed && produitsIncompatibles && produitsIncompatibles.length > 0"
          link
          class="u-pl-lg u-ml-xxs u-mt-xxs"
        >
          <Button
            link
            @click="$refs.popinProduitsIncompatibles.show()"
          >
            Voir les produits non concernés
          </Button>
        </div>
      </Button>
    </template>
    <StateOverlay
      v-if="selected && !disabledComputed"
      :is-loading="isLoading"
      overlay
      message="Calcul de vos avantages en cours..."
    >
      <div
        v-if="isFidCarrefour && hasCarteCarrefour && !isAllowCarteCarrefour && isServiceCompatibleEuroCarrefour"
        class="u-px-md"
      >
        <div class="u-bgcolor-primary-lighter u-p-xs u-mt-xs">
          <p class="u-color-primary-dark u-font-16 u-text-center u-p-xs">
            L'utilisation de votre cagnotte Carrefour sera bientôt possible
          </p>
        </div>
      </div>

      <div class="u-pt-lg u-px-md u-pb-lg25">
        <template v-if="carteCarrefourPayment">
          <p class="u-text-center">Les codes promos ne sont pas cumulables avec un paiement en cagnotte Carrefour</p>
        </template>
        <PaymentMethodMultiPay
          v-if="showEcheancier && !isLoading"
          class="u-mb-md u-mx-lg u-mx-none@md"
          :payment-method="paymentMethod"
          @openPopinMultiPay="openPopinMultiPay"
          @setValiderDisabled="(value) => {validerDisabled = value}"
        />
        <div class="c-payment-method__advantage-fid">
          <PaymentPromoCode
            v-if="useDiscount && !validerDisabled && !carteCarrefourPayment"
            :scroll-to-error="false"
          />
          <PaymentCarteMalin
            v-if="!paymentMethodIsCarteCora && !isPorteurCarteMalin && !carteCoraIsMalinPlus && !showEcheancier && !carteCarrefourPayment && !isFidCarrefour"
            :is-eligible-defis="isEligibleDefis"
          />
          <PaymentCarteCarrefour v-if="!hasCarteCarrefour && isFidCarrefour" />
          <PaymentUseEuroCora
            v-if="displayEuroCora && !validerDisabled && !carteCarrefourPayment && !isFidCarrefour"
            :type-carte="typeCarteEuroCora"
            :montant="montantEuroCora"
            :total-commande="montantTotalTtc"
            :is-eligible-defis="isEligibleDefis"
            @use="useEuroCora"
            @cancel="cancelUseEuroCora"
            @input="val => setInputStatus(val)"
            @validate="processPaiement()"
          />
        </div>

        <template v-if="paymentMethodIsCarteCora && !isPorteurCarteCora">
          <div class="u-mx-md u-mt-lg u-mb-sm">
            <div class="c-payment-method__title c-payment-method__title--dotted">
              <p class="u-mr-xs u-mr-none@sm">
                Votre carte de paiement cora Cpay Mastercard
              </p>
            </div>
          </div>
          <PaymentMethodRegisterCardCora
            class="u-mx-lg20"
            :is-eligible-defis="isEligibleDefis"
          />
        </template>

        <PaymentCardsRegistered
          v-if="(aliasPaiement.length > 0 || hasAliasInOtherMagasin) && isEligibleAliasPaiement"
          :alias-paiement="aliasPaiement"
          :alias-max-par-magasin="aliasMaxParMagasin"
          :shop-alert="hasAliasInOtherMagasin"
          :magasin-name="magasinName"
          @updateAliasPaiement="updateAliasPaiement"
        />

        <div class="u-text-center">
          <Alert
            v-if="restrictionAge"
            variant="error"
            icon-size="md"
            class="u-mt-sm"
          >
            <FormCheckbox v-model="isMajeur">
              Pour finaliser ma commande, je déclare avoir 18 ans ou plus.
            </FormCheckbox>
          </Alert>
          <p class="c-payment-method__cgv">
            En validant je déclare avoir pris connaissance et accepter les
            <Button
              link
              @click="$emit('displayCgv')"
            >
              conditions générales de vente
            </Button>
            .
          </p>
          <Button
            :disabled="(restrictionAge && !isMajeur) || disabledComputed || validerDisabled || aliasNotSelected"
            variant="secondary"
            radius="full"
            size="md"
            @click="processPaiement()"
          >
            Je valide et je paie&nbsp;
            <strong> {{ formatPrix(leftToPay) }}&nbsp;€ </strong>
            <template v-if="paymentMethod.mode_paiement === '3FCB'">
              &nbsp;en 3 fois
            </template>
            <template v-if="paymentMethod.mode_paiement === '4FCB'">
              &nbsp;en 4 fois
            </template>
          </Button>
          <p
            v-if="warning.is_alcool"
            class="c-payment-method__category-exclu"
          >
            Les options de paiement à crédit avec votre carte cora Cpay Mastercard ne sont pas disponibles car votre panier contient de l’alcool. Vous pouvez toujours payer comptant.
          </p>
        </div>
      </div>
    </StateOverlay>
    <Popin
      ref="popinUseEuroCora"
      size="lg"
    >
      <h3 class="u-font-18 u-color-primary u-mt-md u-text-center u-mb-sm">
        N’oubliez pas de valider le montant d'€urocora que vous souhaitez déduire au moment du retrait de votre commande
      </h3>
    </Popin>
    <Popin
      ref="popinProduitsIncompatibles"
      size="lg"
    >
      <PaymentMethodProduitsIncompatibles
        v-if="produitsIncompatibles && produitsIncompatibles.length > 0"
        :produits-incompatibles="produitsIncompatibles"
        :mode-paiement="paymentMethod.libelle_court"
        @cancel="$refs.popinProduitsIncompatibles.hide()"
      />
    </Popin>
  </Card>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/payment-method.scss";
@import "@/assets/styles/components/payment-method-eurocora.scss";
</style>
